import React, {useEffect, useState} from "react";
import "./topbar.css";
import {
    NotificationsNone,
    Settings,
    Logout,
    Person,
    Dashboard,
    Menu as MenuIcon,
    CreditCard
} from "@mui/icons-material";
import {
    Avatar,
    Menu,
    MenuItem,
    useMediaQuery,
    Divider,
    ListItemIcon,
    Box,
    Tooltip,
    IconButton,
    Typography,
    Badge,
    AppBar,
    Toolbar,
    Container,
    useTheme,
    alpha
} from "@mui/material";
import {Link} from "react-router-dom";
import CryptoJS from "crypto-js";
import {ApiDetails, KEY} from "../../Constants";
import { handle401Unauthorized, handle403Forbidden } from "../../utils/authErrorHandler";
import SubscriptionModal from "../SubscriptionModal/SubscriptionModal";
import { NotificationBell } from "../notifications";

export default function Topbar(onMenuToggle) {
    const [forceRefresh, setForceRefresh] = useState(false);
    const [isAgent, setIsAgent] = useState(false);
    const [landlordImage, setLandlordImage] = useState("");
    const [name, setName] = useState("New User");
    const [anchorEl, setAnchorEl] = useState(null);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const isMobile = useMediaQuery("(max-width:800px)");
    const open = Boolean(anchorEl);
    const theme = useTheme();
    const [originalText,] = useState(() => {
        const encryptedData = localStorage.getItem("myMap");
        return encryptedData
            ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
            : "";
    });

    // Read subscription details for proactive payment
    const getSubscriptionInfo = () => {
        try {
            const encryptedSub = localStorage.getItem("subscriptionDetails");
            if (encryptedSub) {
                const decrypted = CryptoJS.AES.decrypt(encryptedSub, KEY).toString(CryptoJS.enc.Utf8);
                if (decrypted) {
                    const parsed = JSON.parse(decrypted);
                    const amount = Math.ceil(parsed?.[1]?.[1] || 0);
                    return {amount};
                }
            }
        } catch (e) {
            console.error("Error reading subscription details:", e);
        }
        return {amount: 0};
    };

    const getExpiryDate = () => {
        try {
            if (originalText) {
                const userData = JSON.parse(originalText);
                if (Array.isArray(userData)) {
                    for (const [key, value] of userData) {
                        if (key === "subscription_expires_at" && value) {
                            const date = new Date(value);
                            date.setMonth(date.getMonth());
                            return (
                                date.getDate() + " " +
                                date.toLocaleString("en-US", {month: "long"}) + ", " +
                                date.getFullYear()
                            );
                        }
                    }
                }
            }
        } catch (e) {
            console.error("Error reading expiry date:", e);
        }
        return null;
    };

    const subscriptionInfo = getSubscriptionInfo();
    const expiryDate = getExpiryDate();

    // Helper to extract initials from a name
    const getInitials = (fullName) =>
        fullName
            .split(" ")
            .map((part) => part[0]?.toUpperCase())
            .join("");

    // Fetch user and image details
    const fetchUserData = async (decryptedData) => {
        try {
            const userDetails = JSON.parse(decryptedData);

            // Safely extract user details with fallback values
            const firstName = userDetails?.[1]?.[1] || "User";
            const lastName = userDetails?.[2]?.[1] || "";
            const email = userDetails?.[5]?.[1] || "";
            const userType = userDetails?.[21]?.[1] || null;

            setName(`${firstName} ${lastName}`.trim() || "User");

            // Fetch landlord image
            if (email) {
                try {
                    const token = userDetails?.find(([key]) => key === 'access_token')?.[1];

                    if (token) {
                        const response = await fetch(`${ApiDetails}pegasus/visionary/authorization/photo`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                "token": token
                            },
                            body: JSON.stringify({email_address: email}),
                        });
                        const data = await response.json();
                        if (response.status === 200 && data.landlord_photo?.base64 !== "No File Found") {
                            setLandlordImage(`data:image/jpeg;base64,${data.landlord_photo.base64}`);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching landlord image:", error);
                }
            }

            // Fetch user type
            try {
                const response = await fetch(`${ApiDetails}pegasus/visionary/extras/user_types`);

                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized("Your session has expired. Please log in again.");
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden("You don't have permission to view user types.");
                    return;
                }

                const data = await response.json();
                if (response.status === 200) {
                    const agentTypeId = data.find((type) => type.description === "AGENT")?.id;
                    setIsAgent(userType === agentTypeId);
                }
            } catch (error) {
                console.error("Error fetching user type:", error);
            }
        } catch (error) {
            console.error("Error parsing user data:", error);
            setName("User");
        }
    };

    // Initial data load
    useEffect(() => {
        try {
            const encryptedData = localStorage.getItem("myMap");
            if (encryptedData) {
                const decryptedData = CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8);
                fetchUserData(decryptedData);
            }
        } catch (error) {
            console.error("Error decrypting user data:", error);
        }
    }, [forceRefresh, isMobile]);

    // Handlers
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/";
    };

    const handleMenuToggle = () => {
        if (onMenuToggle && typeof onMenuToggle === 'function') {
            // console.log("Clicked the menu drawer icon");
            onMenuToggle();
        } else {
            console.error("onMenuToggle is not a function or is undefined");
        }
    };

    const currentPath = window.location.pathname;

    if (["/", "/signup"].includes(currentPath)) {
        return {
            setForceRefresh,
            render: (
                <></>
            )
        };
    }

    return {
        setForceRefresh,
        render: (
            <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: '#f8f9fa',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    color: 'text.primary',
                    zIndex: theme.zIndex.drawer + 1,
                    width: {xs: '100%', sm: `calc(100% - ${isMobile ? 0 : 250}px)`},
                    ml: {xs: 0, sm: `${isMobile ? 0 : 250}px`},
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    transition: 'width 0.3s ease, margin-left 0.3s ease',
                }}
            >
                <Container maxWidth={false}>
                    <Toolbar disableGutters sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 1
                    }}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                            {isMobile && <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleMenuToggle}
                                sx={{mr: 2}}
                            >
                                <MenuIcon/>
                            </IconButton>
                            }
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.9rem' : '1.1rem',
                                    color: '#2c3e50',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Welcome, {name.toUpperCase()}
                            </Typography>
                        </Box>

                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <NotificationBell />
                            <Tooltip title="Account settings">
                                <IconButton
                                    onClick={handleClick}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                        }
                                    }}
                                >
                                    {landlordImage ? (
                                        <Avatar
                                            src={landlordImage}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                border: '2px solid',
                                                borderColor: '#e0e6ed',
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                                color: 'white',
                                                fontWeight: 700,
                                                border: '2px solid',
                                                borderColor: '#e0e6ed',
                                                fontSize: '1rem'
                                            }}
                                        >
                                            {getInitials(name)}
                                        </Avatar>
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            transformOrigin={{horizontal: "right", vertical: "top"}}
                            anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                            PaperProps={{
                                elevation: 3,
                                sx: {
                                    mt: 1.5,
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                                    '&:before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        zIndex: 0,
                                    },
                                },
                            }}
                        >
                            <MenuItem
                                sx={{
                                    py: 1.5,
                                    px: 2.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5
                                }}
                            >
                                <Avatar
                                    src={landlordImage}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        background: landlordImage ? 'transparent' : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                    }}
                                >
                                    {!landlordImage && getInitials(name)}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                        {name}
                                    </Typography>
                                    <Typography variant="body2" sx={{color: 'text.secondary', fontSize: '0.75rem'}}>
                                        {isAgent ? 'Agent Account' : 'User Account'}
                                    </Typography>
                                </Box>
                            </MenuItem>

                            <Divider/>

                            {isAgent && (
                                <MenuItem
                                    onClick={handleClose}
                                    component={Link}
                                    to="/userManagement"
                                    sx={{
                                        py: 1,
                                        color: 'text.primary',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.light, 0.1)
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <Person fontSize="small" color="primary"/>
                                    </ListItemIcon>
                                    User Management
                                </MenuItem>
                            )}

                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    setSubscriptionModalOpen(true);
                                }}
                                sx={{
                                    py: 1,
                                    color: 'text.primary',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.light, 0.1)
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <CreditCard fontSize="small" color="primary"/>
                                </ListItemIcon>
                                Pay Subscription
                            </MenuItem>

                            <Divider/>

                            <MenuItem
                                onClick={handleClose}
                                component={Link}
                                to="/settings"
                                sx={{
                                    py: 1,
                                    color: 'text.primary',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.light, 0.1)
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <Settings fontSize="small" color="primary"/>
                                </ListItemIcon>
                                Settings
                            </MenuItem>

                            <MenuItem
                                onClick={handleLogout}
                                sx={{
                                    py: 1,
                                    color: 'text.primary',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.error.light, 0.1)
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <Logout fontSize="small" color="error"/>
                                </ListItemIcon>
                                Logout
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </Container>
            </AppBar>
            <SubscriptionModal
                open={subscriptionModalOpen}
                onClose={() => setSubscriptionModalOpen(false)}
                subscriptionAmount={subscriptionInfo.amount}
                expired={false}
                expiryDate={expiryDate}
            />
            </>
        )
    };
}